/**
 * Agent Links — persistent CTA buttons pinned between the scrolling
 * chat body and the input footer on Engage + Rail surfaces. Always
 * visible for the entire session; visitors can keep clicking them
 * even after typing — they're CTAs, not starter prompts.
 *
 * Three click types in v1 (dispatched via `handlers` prop):
 *   - send_message: drops a pre-written user message into chat
 *   - open_url:     same-origin SPA nav OR new-tab for cross-origin
 *   - open_content: existing openContent dispatcher (same widget path
 *                   as the agent's `openContent` tool side-effect)
 *
 * `{feature}` substitution happens client-side from the host page's
 * `<meta name="agent-feature">` content. Re-evaluated on every render
 * so SPA navigation updates the label without a reload. Links whose
 * label still contains `{feature}` after substitution (i.e. the meta
 * tag is missing on this page) are hidden — better to drop the link
 * than render "Learn more about {feature}" gibberish.
 *
 * Visibility gates:
 *   - view ∈ {engage, rail}        (spec — Site Agent + Chat Button hide)
 *   - links.length > 0             (no empty container)
 */

import type { AgentLinkResponseItem } from '@showpilot/core';
import type { Signal } from '@preact/signals';

export type AgentLinksHandlers = {
  /** Drop a message into chat as if visitor typed it. */
  onSendMessage: (message: string) => void;
  /** Navigate the host page (same-origin = location, cross-origin = new tab). */
  onOpenUrl: (url: string) => void;
  /**
   * Open a library content item in the viewer panel. Receives the full
   * resolved destination (contentId + server-resolved title / url /
   * thumbnail / extension / embedUrl) so the widget can render the
   * viewer without an extra lookup.
   */
  onOpenContent: (destination: {
    contentId: string;
    title?: string;
    url?: string;
    thumbnailUrl?: string | null;
    contentExtension?: string;
    embedUrl?: string;
  }) => void;
};

export type PageTopic = { url: string; topic: string };

export type AgentLinksProps = {
  bits: {
    links: Signal<AgentLinkResponseItem[]>;
    view: Signal<'site-agent' | 'chat-button' | 'engage' | 'rail' | 'unbound'>;
    /** Bumps on host-page navigation so {feature} re-evaluates. */
    pageNonce: Signal<number>;
    /**
     * Post-chat morph trigger. Pre-chat, links render as full-width
     * stacked CTAs (they're the primary action). After the first user
     * message lands, they collapse to a horizontal chip row so the
     * streaming reply has room to breathe.
     */
    chatStarted: Signal<boolean>;
    /** Composed-opening chips are on screen — static links stand down. */
    openingChips?: Signal<boolean>;
    /**
     * KB-derived URL → topic map from /config. Resolves `{feature}`
     * substitution against the visitor's current page without
     * requiring `<meta name="agent-feature">` on every host page —
     * v1 of {feature} required a meta tag, which customers found
     * unrealistic. Updated 2026-05-19.
     */
    pageTopics: Signal<PageTopic[]>;
  };
  handlers: AgentLinksHandlers;
};

/** Read the host page's `<meta name="agent-feature">` — power-user override. */
function readFeatureMeta(): string {
  if (typeof document === 'undefined') return '';
  const el = document.querySelector<HTMLMetaElement>('meta[name="agent-feature"]');
  return el?.getAttribute('content')?.trim() ?? '';
}

/** Canonical URL for matching: strip protocol, www, trailing slash; lowercase host. */
function canonicalize(rawUrl: string): string {
  try {
    const u = new URL(rawUrl, typeof window === 'undefined' ? 'http://x/' : window.location.href);
    const host = u.hostname.toLowerCase().replace(/^www\./, '');
    const path = u.pathname.replace(/\/+$/, '') || '/';
    return `${host}${path}`;
  } catch {
    return rawUrl.toLowerCase();
  }
}

/** Look up the current page's topic from the KB-derived pageTopics map. */
function readPageTopic(topics: PageTopic[]): string {
  if (typeof window === 'undefined' || topics.length === 0) return '';
  const cur = canonicalize(window.location.href);
  for (const t of topics) {
    if (canonicalize(t.url) === cur) return t.topic.trim();
  }
  return '';
}

/**
 * Substitute `{feature}` in the label. Resolution order:
 *   1. `<meta name="agent-feature">` — explicit per-page override
 *   2. KB pageTopics lookup (current URL → trained title)
 *
 * Returns null when neither resolves — caller hides the link rather
 * than rendering "Learn more about {feature}" gibberish.
 */
function resolveLabel(rawLabel: string, topics: PageTopic[]): string | null {
  const needsFeature = rawLabel.includes('{feature}');
  if (!needsFeature) return rawLabel;
  const feature = readFeatureMeta() || readPageTopic(topics);
  if (!feature) return null;
  return rawLabel.replace(/\{feature\}/g, feature);
}

export function AgentLinks({ bits, handlers }: AgentLinksProps) {
  // Subscribe to signals — `.value` reads register a dependency in the
  // Preact-signals reactive system, so this component re-renders when
  // any of them change. pageNonce is incremented by the SPA nav watcher
  // to force {feature} re-evaluation on host-page navigation.
  const view = bits.view.value;
  const allLinks = bits.links.value;
  const topics = bits.pageTopics.value;
  // Touch pageNonce so SPA nav re-renders this component (re-evaluates
  // {feature} against the new URL). The value itself is unused.
  void bits.pageNonce.value;

  if (view !== 'engage' && view !== 'rail') return null;
  if (allLinks.length === 0) return null;
  // Pre-conversation affordance ONLY (David's real-estate rule): once
  // the visitor commits to the thread, standing links yield entirely —
  // the conversation owns the panel. Same when a composed opening
  // delivered agent-authored intent chips: one chip system at a time,
  // the personalized set wins.
  if (bits.chatStarted.value) return null;
  if (bits.openingChips?.value) return null;

  // Substitute + filter in one pass so the cap applies AFTER hidden
  // links drop. If a customer configured 4 links and one is feature-
  // gated to a page that isn't in the KB's pageTopics, the remaining
  // 3 still show.
  const rendered: Array<{ link: AgentLinkResponseItem; label: string }> = [];
  for (const link of allLinks) {
    const label = resolveLabel(link.label, topics);
    if (label === null) continue;
    rendered.push({ link, label });
    if (rendered.length >= 4) break;
  }
  if (rendered.length === 0) return null;

  const onClick = (link: AgentLinkResponseItem): void => {
    const d = link.destination;
    if (d.type === 'send_message') {
      // Substitute `{feature}` in the message body too — same resolution
      // as the visible label. Without this the chip showed "What would
      // you like to know about Pricing?" but the click sent the raw
      // template "What would you like to know about {feature}?" → the
      // LLM saw a literal placeholder and called it out as a bug in
      // the visitor's message.
      const resolvedMessage = resolveLabel(d.message, topics) ?? d.message;
      handlers.onSendMessage(resolvedMessage);
    } else if (d.type === 'open_url') {
      handlers.onOpenUrl(d.url);
    } else if (d.type === 'open_content') {
      handlers.onOpenContent({
        contentId: d.contentId,
        title: d.title,
        url: d.url,
        thumbnailUrl: d.thumbnailUrl ?? null,
        contentExtension: d.contentExtension,
        embedUrl: d.embedUrl,
      });
    }
  };

  // The quiet chip row is the ONLY look now — the full-width filled
  // bars read as an IVR menu and shouted over the agent (David
  // 2026-07-14, after comparing live agents in market).
  const wrapperClass = 'sp-agent-links sp-agent-links--compact';
  const btnClass = 'sp-agent-link sp-agent-link--compact';
  return (
    <div class={wrapperClass} role="group" aria-label="Quick actions">
      {rendered.map(({ link, label }) => (
        <button
          key={link.id}
          type="button"
          class={btnClass}
          onClick={() => onClick(link)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
